import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input, OnChanges, ViewChild } from '@angular/core';
import { GanttBarClickEvent, GanttDate, GanttGroup, GanttItem, GanttItemType, GanttLineClickEvent, GanttSelectedEvent, GanttTableItemClickEvent, GanttToolbarOptions, GanttView, GanttViewOptions, GanttViewType, NgxGanttComponent, NgxGanttModule } from '@worktile/gantt';
import { Event } from '../../../../api/scheduler-service';
import { GanttEventViewComponent } from '../gantt-event-view/gantt-event-view.component';
import { MatDialog } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as htmlToImage from 'html-to-image';

export class TimeRange {
  constructor (public from : string, public to : string) {

  }
};

@Component({
  selector: 'scheduler-events-gantt',
  imports: [CommonModule, NgxGanttModule, MatTooltipModule],
  templateUrl: './scheduler-events-gantt.component.html',
  styleUrl: './scheduler-events-gantt.component.css'
})
export class SchedulerEventsGanttComponent implements OnChanges{

  @ViewChild('ganttContainer', { static: false }) ganttRef!: ElementRef;
  @ViewChild('gantt', { static: false }) gantt!: NgxGanttComponent;
 
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async scrollElement(element: HTMLElement, direction: 'horizontal' | 'vertical'): Promise<void> {
    const step = 80;
    const overscroll = 500;
    const maxScroll = direction === 'vertical'
      ? element.scrollHeight - element.clientHeight
      : element.scrollWidth - element.clientWidth;
  

    console.log('Scroll height: ' + element.scrollHeight + ' Client Height: ' + element.clientHeight); 
    // Scroll in steps
    for (let pos = 0; pos <= maxScroll + overscroll; pos += step) {
      if (direction === 'vertical') {
        element.scrollTop = pos;
      } else {
        element.scrollLeft = pos;
      }
      await this.delay(10); // Give rendering time
    }
  
    // 🔁 Pause at bottom for virtual scroll to catch up
    if (direction === 'vertical') {
      element.scrollTop = element.scrollHeight;
    } else {
      element.scrollLeft = element.scrollWidth;
    }
  
    await this.delay(10); // Allow final paint
  
    // Reset back to top/left
    element.scrollTop = 0;
    element.scrollLeft = 0;

  }

  async downloadGanttImage(scheduleName?: string): Promise<void> {
    const wrapper = this.ganttRef.nativeElement as HTMLElement;

    const horizontalContainer = wrapper.querySelector('.gantt-calendar.gantt-calendar-grid') as HTMLElement;
    if (!horizontalContainer) {
      console.error('Horizontal scroll container not found');
      return;
    }

    const verticalContainer = wrapper.querySelector(
      '.cdk-virtual-scroll-viewport.gantt-virtual-scroll-viewport'
    ) as HTMLElement;

    await this.scrollElement(verticalContainer, 'vertical');
    await this.scrollElement(horizontalContainer, 'horizontal');

    const originalHeight = wrapper.style.height;
    const originalWidth = wrapper.style.width;

    wrapper.style.height = (verticalContainer.scrollHeight + 60) + 'px';
    wrapper.style.width = (horizontalContainer.scrollWidth + 60) + 'px';

    try {
      // pixelRatio:2 → crisp capture on retina + when printed.
      // backgroundColor honours dark-mode page background so transparent gaps
      // around the chart don't paint white when the user is on dark theme.
      const dataUrl = await htmlToImage.toPng(this.ganttRef.nativeElement, {
        pixelRatio: 2,
        backgroundColor: getComputedStyle(document.documentElement)
          .getPropertyValue('--bg-page').trim() || undefined,
        cacheBust: true,
      });

      const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const slug = (scheduleName || 'schedule').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      const link = document.createElement('a');
      link.download = `gantt-${slug}-${ts}.png`;
      link.href = dataUrl;
      link.click();
    } catch (err) {
      console.error('Failed to export Gantt image', err);
    } finally {
      // ALWAYS restore — pre-fix this leaked, leaving the gantt permanently
      // expanded across the whole viewport after the first export.
      wrapper.style.height = originalHeight;
      wrapper.style.width = originalWidth;
    }
  }
  onLineClicked(event: GanttLineClickEvent<unknown>) {
    this.dialog.open(GanttEventViewComponent, {
      panelClass: 'bw-dialog-panel',
      width: "60%",
      data: { item: event.source }
    });
  }
  
  protected dialog: MatDialog = inject(MatDialog)

  onBarClicked(event: GanttBarClickEvent<unknown>) {
    this.dialog.open(GanttEventViewComponent, {
      panelClass: 'bw-dialog-panel',
      width: "60%",
      data: { item: event.item }
    });
  }

  onChildClicked(item: Event) {
    this.dialog.open(GanttEventViewComponent, {
      panelClass: 'bw-dialog-panel',
      width: "60%",
      data: { item: item }
    });
  }

  onItemClick(event: GanttTableItemClickEvent<unknown>) {
    this.gantt.scrollToDate(new GanttDate(event.current?.start));
  }

  @Input() events: Event[] = [];
  @Input() viewType: GanttViewType = GanttViewType.hour;
  @Input() eventDependency : boolean = false;

  /**
   * Local-preview simulation. The scheduler-service's `next()` only predicts
   * WHEN events fire; it has no model of how long they take. When the user
   * dials a duration on the schedule page's Simulation tab, we synthesize a
   * (startTime, endTime) per execution so the gantt renders bars instead of
   * point-in-time markers. Failure rate paints a deterministic-but-shuffled
   * subset of bars red. Drift shifts every start by a constant offset.
   *
   * `durationMs` of 0 disables the override entirely — bars fall back to the
   * server's reported endTime (typically equal to startTime, i.e. zero-width).
   */
  @Input() simulationDurationMs: number = 0;
  @Input() simulationJitterPercent: number = 0;
  @Input() simulationFailurePercent: number = 0;
  @Input() simulationDriftMs: number = 0;

  start: Date = new Date();
  groups: GanttGroup[] = [];
  items: GanttItem[] = [];
  toolbarOptions : GanttToolbarOptions = 
    { viewTypes: [  GanttViewType.day, 
                    GanttViewType.hour, 
                    GanttViewType.week, 
                    GanttViewType.month, 
                    GanttViewType.quarter, 
                    GanttViewType.year] 
    };
  
  today : Date = new Date();
  
  viewOptions: GanttViewOptions = {
    start: new GanttDate(new Date()),
    end: new GanttDate(new Date().setDate(this.today.getDate()+ 1)),
    min: new GanttDate(new Date()),
    max: new GanttDate(new Date().setDate(this.today.getDate()+ 1)),
    cellWidth: 60
  };

  public taskSettings: any = {
    id: 'id',
    name: 'name',
    startDate: 'startDate',
    endDate: 'endDate'
  };


  onViewChanged(gv : GanttView) {
    if (gv.viewType != this.viewType) {
      this.viewType = gv.viewType;
      this.ngOnChanges();
    }
  }

  ngOnChanges() {

    this.groups = [
      { id: '1', title: 'Events' }
    ];

    //this.items = [];
    var eventId : number = 1;
    var execIndex : number = 0;        // running counter so failure painting is deterministic across all executions

    var tempItems = [];
    for (var event of this.events) {
      let item : GanttItem = {
        id: String(event.eventId),
        title: `Event: ${event.originalExecutionDateTime}`,
        start: this.toDate(event.originalExecutionDateTime),
        end: this.endDate(event),
        group_id: '1',
        expanded: true,
        expandable: true,
        type: GanttItemType.bar,
      };

      tempItems.push(item);
      item.children = [];
      for (var execution of (event.executions ?? [])) {
        const simulated = this.applySimulation(execution.startTime, execution.endTime, execIndex);
        const failed = this.isSimulatedFailure(execIndex);
        let child : GanttItem = {
          id: String(execution.executionId),
          title: failed
                ? `Execution: ${execution.startTime} (simulated FAILURE)`
                : `Execution: ${execution.startTime}`,
          start: simulated.start,
          end: simulated.end,
          group_id: '1',
          expanded: true,
          expandable: true,
          type: GanttItemType.range,
          color: failed ? '#d73a49' : undefined,    // ngx-gantt accepts a per-item bar colour; lets us paint failures red
        };


        item.children.push(child);
        execIndex++;
      }

      // Recompute the parent bar's start/end so a simulated duration extends it
      // past the raw originalExecutionDateTime — without this the parent shows
      // as a single tick even when its children span minutes.
      if (item.children.length > 0) {
        const starts = item.children.map(c => c.start).filter(Boolean) as Date[];
        const ends   = item.children.map(c => c.end).filter(Boolean)   as Date[];
        if (starts.length) item.start = new Date(Math.min(...starts.map(d => d.getTime())));
        if (ends.length)   item.end   = new Date(Math.max(...ends.map(d => d.getTime())));
      }

      eventId++;
      if (tempItems.length > 0) {
        var ganttItem : GanttItem = tempItems[0];
        setTimeout(() => this.gantt.scrollToDate(new GanttDate(ganttItem.start? ganttItem.start : new Date())), 0 );
      }

    }

    if ( this.events.length > 0) {
      this.viewOptions = {
        start: new GanttDate(new Date()),
        end: new GanttDate(this.endDate(this.events[this.events.length -1])),
        min: new GanttDate(new Date()),
        max: new GanttDate(this.endDate(this.events[this.events.length -1])),
        cellWidth: 60
      };
    } else {
      this.viewOptions = {
        start: new GanttDate(new Date()),
        end: new GanttDate(new Date()),
        min: new GanttDate(new Date()),
        max: new GanttDate(new Date()),
        cellWidth: 60
      };
    }

    if (this.eventDependency) {
      for (var index =0; index < (tempItems.length - 1); index++) {
        tempItems[index].links = [tempItems[index+1].id];
      }
    }

    for (var index =0; index < tempItems.length; index++) {
      var eventItem = tempItems[index];
      if (eventItem.children) {
        for (var x = 0; x < eventItem.children.length -1;x++) {
          eventItem.children[x].links = [eventItem.children[x+1].id];
        }
      }
    }

    this.items = tempItems;

  }

  private endDate(event: Event) : Date | undefined {
    const executions = event.executions ?? [];
    return executions.length === 0 ? undefined : this.toDate(executions[executions.length - 1].endTime);
  }

  private toDate(value: string | undefined): Date | undefined {
    return value ? new Date(value) : undefined;
  }

  /**
   * Combines drift, duration, and jitter into a synthetic (start, end) for
   * one execution. When `simulationDurationMs` is 0 we leave the server-
   * supplied times alone — the caller is "previewing real schedule output"
   * rather than simulating workload. Jitter is deterministic per-index so
   * the gantt is stable as the user nudges other inputs.
   */
  private applySimulation(rawStart?: string, rawEnd?: string, index: number = 0): { start?: Date, end?: Date } {
    const start = this.toDate(rawStart);
    if (!start) return { start: undefined, end: this.toDate(rawEnd) };
    const drifted = new Date(start.getTime() + (this.simulationDriftMs || 0));
    if (!this.simulationDurationMs || this.simulationDurationMs <= 0) {
      return { start: drifted, end: this.toDate(rawEnd) };
    }
    const jitter = this.simulationJitterPercent
            ? this.simulationDurationMs * (this.simulationJitterPercent / 100) * (this.deterministicNoise(index) - 0.5) * 2
            : 0;
    const ms = Math.max(1, Math.round(this.simulationDurationMs + jitter));
    return { start: drifted, end: new Date(drifted.getTime() + ms) };
  }

  /** True when execution #index should render as a simulated failure. Uses
   *  a separate noise channel from the duration jitter so a 50/50 pattern
   *  doesn't accidentally correlate with "the slow ones." */
  private isSimulatedFailure(index: number): boolean {
    if (!this.simulationFailurePercent || this.simulationFailurePercent <= 0) return false;
    if (this.simulationFailurePercent >= 100) return true;
    return this.deterministicNoise(index, 0xa1b2c3) < (this.simulationFailurePercent / 100);
  }

  /** Splittable mix64 — returns a stable [0, 1) noise value for an integer key.
   *  Stable so the user nudging a slider doesn't reshuffle which bars are red. */
  private deterministicNoise(index: number, salt: number = 0): number {
    let z = ((index + 1) * 0x9e3779b1 ^ salt) >>> 0;
    z = (z ^ (z >>> 16)) * 0x85ebca6b >>> 0;
    z = (z ^ (z >>> 13)) * 0xc2b2ae35 >>> 0;
    z = (z ^ (z >>> 16)) >>> 0;
    return (z & 0xffffffff) / 0xffffffff;
  }

}
